import { StageWorkerBridge } from './StageWorkerBridge'
import type { StageController, StageOptions } from './types'

/**
 * Instantiates the 3D Stage.
 * Prefers the dedicated Web Worker + OffscreenCanvas architecture to offload
 * Three.js rendering loop, post-processing passes, and matrix updates from the main DOM thread.
 * Automatically falls back to the main thread Stage if OffscreenCanvas or Web Workers
 * are unsupported (or if disabled via ?worker=0 for profiling/comparison).
 */
/** True when the stage runs in the worker, so building it costs the main thread almost nothing. */
export function supportsOffscreenStage(): boolean {
  const allowWorker =
    !location.search.includes('no-worker=1') &&
    !location.search.includes('worker=0')

  return (
    allowWorker &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function' &&
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined'
  )
}

export async function createStage(
  canvas: HTMLCanvasElement,
  opts: StageOptions,
  onContextLost?: () => void,
): Promise<StageController> {
  if (supportsOffscreenStage()) {
    try {
      return new StageWorkerBridge(canvas, opts, onContextLost)
    } catch (err) {
      console.warn('[stage] OffscreenCanvas worker initialization failed; falling back to main thread.', err)
    }
  }

  const { Stage } = await import('./Stage')
  return new Stage(canvas, opts)
}
